import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { useAviso } from '../../components/Aviso';
import { api, ApiError } from '../../lib/api';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { DENOMINACOES_BRL, totalGradinha, formatMoeda, formatHoraSP } from '@fiodecobre/shared';
import { AlertTriangle, Lock } from 'lucide-react';

interface UsuarioLista {
  id: number;
  nome: string;
  login: string;
  perfil: string;
  ativo: boolean;
  opera_caixa: boolean;
}

interface FecharResultado {
  caixa_nome: string;
  valor_abertura: number;
  recebido: number;
  suprimentos: number;
  sangrias: number;
  valor_esperado: number;
  valor_contado: number;
  divergencia: number;
  transferencia: { destino_nome: string; valor: number } | null;
  troco_final: number;
  sucessor: { id: number; nome: string } | null;
}

// Linha do grid de movimentos do turno (`GET /turno/:id/extrato?completo=1`). `valor` ja vem com
// sinal: venda/suprimento/transferencia recebida positivos, sangria/transferencia enviada negativos.
interface MovimentoTurno {
  ts: string;
  tipo: string;
  descricao: string;
  valor: number;
}

interface PassagemResultado {
  caixa_nome: string;
  herdado: number;
  recontado: number;
  divergencia_abertura: number;
}

interface TurnoModalProps {
  modo: 'abrir' | 'fechar' | 'confirmar' | 'conferir';
  caixa: { id: number; nome: string };
  sessaoId?: number;            // obrigatorio no fechar, confirmar e conferir
  usuarioId: number | null;     // pra excluir a si mesmo da lista de sucessores
  destinosTransferencia?: Array<{ id: number; nome: string }>;  // gavetas + Cofre (modo fechar)
  onFechar: () => void;
  onConcluido: () => void;      // recarrega as sessoes no pai
  onAbrirExtrato?: () => void;  // no modo conferir: abre a lupa (extrato) quando ha diferenca
  // No modo conferir, quando a gaveta CONFERE: leva direto pro fechamento. Existe porque conferir
  // no fim do expediente e depois esquecer de fechar era o caminho natural do erro — a operadora
  // conta, ve "tudo certo", clica em "Concluir" e vai embora achando que fechou (foi o que houve
  // com a gaveta da Rafaela em 18/07/2026). O turno ficava aberto e travava a abertura seguinte.
  onFecharTurnoAgora?: () => void;
}

// Linha da gradinha: quantidade contada por denominacao (contagem CEGA — nunca mostra o
// esperado antes do envio). O subtotal/total sai do proprio shared (totalGradinha).
// DA-127: a gaveta de dinheiro fecha SO o dinheiro da pessoa — a conferencia das contas
// digitais virou acao independente (botao "Conferir recebimento", so admin/gerente).
export function TurnoModal({ modo, caixa, sessaoId, usuarioId, destinosTransferencia, onFechar, onConcluido, onAbrirExtrato, onFecharTurnoAgora }: TurnoModalProps) {
  const toast = useToast();
  const aviso = useAviso();
  const [gradinha, setGradinha] = useState<Record<string, number>>({});
  const [sucessorId, setSucessorId] = useState<string>('');
  const [usuarios, setUsuarios] = useState<UsuarioLista[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<FecharResultado | null>(null);
  const [resultadoPassagem, setResultadoPassagem] = useState<PassagemResultado | null>(null);
  const [resultadoConferencia, setResultadoConferencia] = useState<{ esperado: number; contado: number; divergencia: number } | null>(null);
  // Estado atual da gaveta (modo conferir, NAO cego): esperado-ate-agora + detalhe.
  const [estado, setEstado] = useState<{ esperado: number; abertura: number; recebido: number; suprimentos: number; sangrias: number } | null>(null);
  // Saldo fisico do turno anterior (modo abrir): avisa o operador pra incluir na contagem.
  const [saldoAnterior, setSaldoAnterior] = useState<number | null>(null);
  // Transferencia no fechamento (separar as vendas pro Cofre). Pre-selecionada por padrao no fechar.
  const [transfDestino, setTransfDestino] = useState<string>('');
  const [transfValor, setTransfValor] = useState<number>(0);
  // Fechar: marca que a operadora editou o valor a separar a mao (para de auto-sugerir contado-abertura).
  const [transfTouched, setTransfTouched] = useState(false);
  // PORTAO da abertura: quando o contado nao bate com o troco deixado pelo turno anterior, o backend
  // BLOQUEIA (409) e devolve este payload. A operadora escolhe um dos 3 caminhos sem mexer na contagem.
  const [bloqueio, setBloqueio] = useState<{ troco_deixado: number; contado: number; divergencia: number; tipo: 'falta' | 'sobra'; mensagem: string } | null>(null);
  const [bloqueioDestino, setBloqueioDestino] = useState<string>('');
  // PORTAO do FECHAMENTO (DA-334): se a gaveta contada nao bate com o esperado, o Fechar NAO fecha
  // direto — abre um painel de solucao (recontar / registrar a diferenca e fechar). Enquanto setado,
  // o painel de solucao renderiza no lugar do formulario.
  const [divergFecharPendente, setDivergFecharPendente] = useState<{ contado: number; esperado: number; divergencia: number } | null>(null);
  // Grid de movimentos do turno INTEIRO, exibido na coluna da direita no modo fechar (DA-396). A
  // lupa (`onAbrirExtrato`) segue existindo pro caso de investigacao — ela corta na ultima
  // conferencia que bateu e mostra o historico de conferencias; aqui a pergunta e outra ("o que
  // entrou nesta gaveta"), entao pede `?completo=1`.
  const [movimentos, setMovimentos] = useState<MovimentoTurno[] | null>(null);

  useEffect(() => {
    if (modo !== 'fechar') return;
    api.get<UsuarioLista[]>('/api/usuarios')
      .then((lista) => setUsuarios(lista))
      .catch(() => { /* lista de sucessores e opcional */ });
  }, [modo]);

  // Conferir (meio do turno) e Fechar (fim) sao TRANSPARENTES (DA-334 reverte a contagem cega):
  // carregam o esperado pra mostrar de cara e a operadora conta vendo a diferenca ao vivo. No fechar
  // ja pre-seleciona o Cofre como destino da separacao das vendas do dia.
  useEffect(() => {
    if ((modo !== 'conferir' && modo !== 'fechar') || !sessaoId) return;
    api.get<{ esperado: number; abertura: number; recebido: number; suprimentos: number; sangrias: number; gradinha_abertura: Record<string, number> | null }>(`/api/caixa/turno/${sessaoId}/estado`)
      .then((e) => {
        setEstado(e);
        if (modo === 'fechar' && destinosTransferencia) {
          const cofre = destinosTransferencia.find((d) => /cofre/i.test(d.nome));
          if (cofre) setTransfDestino(String(cofre.id));
        }
      })
      .catch((e) => aviso.erro(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, sessaoId]);

  // Fechar: carrega o extrato do turno INTEIRO pro grid da direita. Falha nao bloqueia o
  // fechamento — o grid mostra o aviso e a contagem segue (o dinheiro nao espera a lista).
  useEffect(() => {
    if (modo !== 'fechar' || !sessaoId) return;
    api.get<{ movimentos: MovimentoTurno[] }>(`/api/caixa/turno/${sessaoId}/extrato`, { query: { completo: 1 } })
      .then((r) => setMovimentos(r.movimentos ?? []))
      .catch(() => setMovimentos([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, sessaoId]);

  // Ao abrir turno: avisa se o turno anterior deixou saldo na gaveta.
  useEffect(() => {
    if (modo !== 'abrir') return;
    api.get<{ saldo: number | null }>(`/api/caixa/saldo-anterior/${caixa.id}`)
      .then((r) => setSaldoAnterior(r.saldo))
      .catch(() => { /* nao bloqueia */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, caixa.id]);

  const sucessores = useMemo(
    () => usuarios.filter((u) =>
      u.ativo && u.id !== usuarioId &&
      (['admin', 'gerente', 'caixa'].includes(u.perfil) || u.opera_caixa),
    ),
    [usuarios, usuarioId],
  );

  // Todos os modos com gaveta contam pela gradinha (o FECHAR volta a contar — DA-334, transparente).
  const total = totalGradinha(gradinha);

  // Fechar: sugere separar pro Cofre "as vendas do dia" = esperado − abertura (deixa na gaveta o
  // MESMO troco que abriu), COM TETO no que foi contado — nao da pra separar cedula que nao esta
  // na gaveta. Editavel; para de sugerir assim que a operadora edita o valor a mao.
  //
  // ⚠️ Ate 15/08/2026 nao havia o teto: a sugestao era ancorada no ESPERADO, enquanto a validacao
  // do `enviarFechamento` mede contra o CONTADO. Duas ancoras pro mesmo numero, e o buraco aparecia
  // justamente na contagem que mais precisa fechar — a gaveta vazia. Caso real (turno de 30/07 do
  // Movimento de Caixa, esperado 993,00, abertura 479,00): contando 0,00 a sugestao ficava nos
  // 514,00 herdados do esperado e o Fechar respondia "o valor a separar nao pode ser maior que o
  // que voce contou". O portao certo ("Registrar a diferenca e fechar") estava aceso na tela e era
  // INALCANCAVEL — beco sem saida numa gaveta que precisava ser fechada.
  //
  // `total` nas deps de proposito: enquanto ninguem editar o valor a mao, a sugestao ACOMPANHA a
  // contagem. Contou 0 -> sugere 0 -> `querTransferir` fica falso e o fechamento segue pro portao
  // da divergencia, que e onde a falta tem que ser registrada.
  useEffect(() => {
    if (modo !== 'fechar' || transfTouched || !estado) return;
    setTransfValor(Math.max(0, Math.min(total, +(estado.esperado - estado.abertura).toFixed(2))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, transfTouched, estado, total]);
  // Diferenca ao vivo do fechamento (transparente): contado − esperado.
  const diffFechar = estado ? +(total - estado.esperado).toFixed(2) : 0;
  // Tolerancia do portao de fechamento = R$1 (espelha DIVERGENCIA_ALERTA_MIN do backend). Tambem
  // absorve o centavo da calibracao do Pix pago em dinheiro, que nao e contavel em moeda (ver #1 das
  // pontas do caixa). Abaixo disso, "bate" e fecha direto; igual ou acima, abre o painel de solucao.
  const TOLERANCIA_FECHAR = 1;

  function setQtd(cents: number, valor: string) {
    const n = Math.max(0, Math.floor(Number(valor) || 0));
    setGradinha((g) => ({ ...g, [String(cents)]: n }));
  }

  async function confirmar() {
    if (modo === 'abrir') {
      // Não abre sem contar: a quantidade de cédulas e moedas é obrigatória (evita abrir
      // zerado por engano e perder o troco que ficou na gaveta do turno anterior).
      if (totalGradinha(gradinha) <= 0) {
        aviso.erro('Informe a quantidade de cédulas e moedas antes de abrir o caixa.');
        return;
      }
      setEnviando(true);
      try {
        await api.post('/api/caixa/abrir-turno', { caixa_id: caixa.id, gradinha });
        toast.sucesso('Turno aberto');
        onConcluido();
        onFechar();
      } catch (e: any) {
        // PORTAO: divergencia na contagem -> backend bloqueia (409). Mostra os 3 caminhos.
        if (e instanceof ApiError && e.status === 409 && e.corpo?.bloqueio) {
          setBloqueio({
            troco_deixado: Number(e.corpo.troco_deixado),
            contado: Number(e.corpo.contado),
            divergencia: Number(e.corpo.divergencia),
            tipo: e.corpo.tipo,
            mensagem: e.corpo.mensagem,
          });
          // Pre-seleciona o Cofre como destino sugerido pra reconciliar.
          const cofre = destinosTransferencia?.find((d) => /cofre/i.test(d.nome));
          if (cofre) setBloqueioDestino(String(cofre.id));
        } else {
          aviso.erro(e.message);
        }
      } finally {
        setEnviando(false);
      }
      return;
    }

    if (modo === 'conferir') {
      setEnviando(true);
      try {
        const r = await api.post<{ ok: boolean; esperado: number; contado: number; divergencia: number }>(
          `/api/caixa/conferir-turno/${sessaoId}`, { gradinha },
        );
        setResultadoConferencia({ esperado: r.esperado, contado: r.contado, divergencia: r.divergencia });
        onConcluido();
      } catch (e: any) {
        aviso.erro(e.message);
      } finally {
        setEnviando(false);
      }
      return;
    }

    if (modo === 'confirmar') {
      setEnviando(true);
      try {
        const r = await api.post<PassagemResultado & { ok: boolean }>(`/api/caixa/confirmar-abertura/${sessaoId}`, { gradinha });
        setResultadoPassagem(r);   // revela a divergencia da passagem SO depois do envio
        onConcluido();
      } catch (e: any) {
        aviso.erro(e.message);
      } finally {
        setEnviando(false);
      }
      return;
    }

    // Fechar a gaveta (DA-334, transparente): conta cedula a cedula VENDO o esperado e a diferenca ao
    // vivo.
    //
    // Contagem ZERO fecha (DA-343). Aqui NAO existe mais a trava `totalGradinha <= 0` que o modo
    // `abrir` mantem: ela confundia "nao contei" com "contei e a gaveta esta vazia". Gaveta vazia e
    // um FATO — barrar so prendia o turno aberto pra sempre, que foi o caso real da gaveta que
    // amanheceu com falta legitima. Zero nao escapa de nada: cai no portao logo abaixo (a diferenca
    // vira o esperado inteiro, muito acima da tolerancia) e a operadora e obrigada a escolher
    // Recontar ou Registrar a diferenca e fechar. A falta fica assinada, nunca escondida.
    // PORTAO do fechamento: se nao bate (>= tolerancia), NAO fecha direto — abre o painel de solucao
    // (Recontar / Registrar a diferenca e fechar). "Nao permitir fechar com erro" (decisao do Ruan).
    const esperado = estado?.esperado ?? 0;
    const diff = +(totalGradinha(gradinha) - esperado).toFixed(2);
    if (Math.abs(diff) >= TOLERANCIA_FECHAR) {
      setDivergFecharPendente({ contado: totalGradinha(gradinha), esperado, divergencia: diff });
      return;
    }
    await enviarFechamento();
  }

  // Envia o fechamento de fato (contado + gradinha + transferencia). Chamado quando a gaveta BATE ou
  // quando a operadora escolhe "Registrar a diferenca e fechar" no painel de solucao.
  async function enviarFechamento() {
    const querTransferir = transfValor > 0.005;
    if (querTransferir && !transfDestino) { aviso.erro('Escolha o destino da transferência'); return; }
    if (querTransferir && transfValor > total + 0.005) {
      // A mensagem NOMEIA os dois numeros e diz onde mexer. Sem isso ela e um beco: o operador le
      // "nao pode ser maior" sem saber quanto esta la nem qual campo mudar — foi o que aconteceu
      // em 15/08/2026, com o valor herdado do esperado numa gaveta contada em zero.
      aviso.erro(
        `Você contou ${formatMoeda(total)} e o campo "Separar para" está com ${formatMoeda(transfValor)}. `
        + 'Não dá pra separar mais dinheiro do que tem na gaveta — baixe esse valor ou escolha "— Não transferir —".',
      );
      return;
    }
    const transferencia = querTransferir
      ? { caixa_destino_id: Number(transfDestino), valor: transfValor }
      : undefined;

    // Manda o CONTADO (total da gradinha) + a gradinha: o backend grava a contagem e a divergencia,
    // registra a transferencia (se pedida), dispara o alerta (>= R$1) e deixa o troco liquido adiante.
    setEnviando(true);
    try {
      const r = await api.post<FecharResultado & { ok: boolean }>(`/api/caixa/fechar-turno/${sessaoId}`, {
        contado: totalGradinha(gradinha),
        gradinha,
        sucessor_id: sucessorId ? Number(sucessorId) : null,
        transferencia,
      });
      setDivergFecharPendente(null);
      setResultado(r);          // revela esperado x contado + a divergencia
      onConcluido();
    } catch (e: any) {
      aviso.erro(e.message);
    } finally {
      setEnviando(false);
    }
  }

  // Caminho ASSUMIR: abre registrando a divergencia EM ABERTO (vai pra faixa de aviso pro admin bater).
  async function assumirDiferenca() {
    setEnviando(true);
    try {
      await api.post('/api/caixa/abrir-turno', { caixa_id: caixa.id, gradinha, resolucao: 'assumir' });
      toast.sucesso('Turno aberto — a diferença ficou registrada pra gerência conferir');
      onConcluido();
      onFechar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setEnviando(false); }
  }

  // Caminho TRANSFERIR: registra a transferencia que reconcilia (SOBRA -> manda o excesso pro Cofre;
  // FALTA -> registra que o dinheiro JA foi pra outra conta). So entao abre.
  async function transferirEAbrir() {
    if (!bloqueio) return;
    if (!bloqueioDestino) { aviso.erro('Escolha a conta de destino'); return; }
    setEnviando(true);
    try {
      await api.post('/api/caixa/abrir-turno', {
        caixa_id: caixa.id, gradinha, resolucao: 'transferir',
        transferencia: { caixa_destino_id: Number(bloqueioDestino), valor: Math.abs(bloqueio.divergencia) },
      });
      toast.sucesso('Transferência registrada (aguardando validação) — turno aberto');
      onConcluido();
      onFechar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setEnviando(false); }
  }

  // Painel do PORTAO: a abertura foi bloqueada porque o contado nao bate com o troco deixado.
  // 3 caminhos: recontar (volta pra gradinha), transferir (reconcilia) ou assumir (registra a falta).
  if (bloqueio) {
    const falta = bloqueio.tipo === 'falta';
    const diff = Math.abs(bloqueio.divergencia);
    return (
      <Modal aberto onFechar={onFechar} titulo={`Abrir turno — ${caixa.nome}`} largura={560}
        rodape={<button type="button" className="btn-secundario" onClick={onFechar} disabled={enviando}>Cancelar</button>}
      >
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{
            padding: '12px 14px', borderRadius: 8, backgroundColor: '#ef4444',
            borderLeft: '6px solid #b91c1c', color: '#000', fontWeight: 700,
          }}>
            {bloqueio.mensagem}
          </div>

          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Você contou agora</span><span className="cel-num">{formatMoeda(bloqueio.contado)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Foi deixado no fechamento</span><span className="cel-num">{formatMoeda(bloqueio.troco_deixado)}</span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', marginTop: 4, padding: '8px 12px',
              borderRadius: 8, backgroundColor: '#fde68a', color: '#000', fontWeight: 700,
            }}>
              <span>{falta ? 'FALTOU' : 'SOBROU'}</span><span className="cel-num">{formatMoeda(diff)}</span>
            </div>
          </div>

          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
            O valor que você contou está certo e não muda. Escolha o que fazer com a diferença pra abrir:
          </p>

          {/* a) Recontar — volta pra contagem */}
          <button type="button" className="btn-secundario" onClick={() => setBloqueio(null)} disabled={enviando}
            style={{ textAlign: 'left', padding: '10px 14px' }}>
            <strong>Recontar a gaveta</strong>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Volta pra contagem e refaz cédula a cédula.</div>
          </button>

          {/* b) Transferir — reconcilia */}
          <div style={{ border: '1px solid var(--input-border, #e5e7eb)', borderRadius: 8, padding: '10px 14px', display: 'grid', gap: 8 }}>
            <div>
              <strong>{falta ? 'Registrar que o dinheiro foi pra outra conta' : 'Transferir o excesso pra outra conta'}</strong>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {falta
                  ? `Registra que ${formatMoeda(diff)} já saíram da gaveta pra outra conta (ex.: o turno anterior mandou pro Cofre e não lançou).`
                  : `Manda os ${formatMoeda(diff)} a mais pra outra conta (ex.: Cofre). Fica aguardando validação da gerência.`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={bloqueioDestino} onChange={(e) => setBloqueioDestino(e.target.value)} style={{ flex: 1, fontSize: 13 }}>
                <option value="">— Conta de destino —</option>
                {destinosTransferencia?.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </select>
              <span className="cel-num" style={{ fontWeight: 700 }}>{formatMoeda(diff)}</span>
              <button type="button" className="btn-primario" onClick={transferirEAbrir} disabled={enviando || !bloqueioDestino} style={{ fontSize: 13 }}>
                {enviando ? '...' : 'Transferir e abrir'}
              </button>
            </div>
          </div>

          {/* c) Assumir — abre com a divergencia em aberto */}
          <button type="button" className="btn-secundario" onClick={assumirDiferenca} disabled={enviando}
            style={{ textAlign: 'left', padding: '10px 14px' }}>
            <strong>Assumir a diferença e abrir</strong>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Abre o turno com a {falta ? 'falta' : 'sobra'} de {formatMoeda(diff)} registrada. A gerência confere depois (fica no aviso do sistema).
            </div>
          </button>
        </div>
      </Modal>
    );
  }

  // PORTAO do FECHAMENTO (DA-334): a gaveta contada nao bateu com o esperado. Nao fecha com erro —
  // 2 caminhos: recontar (volta pra contagem) ou registrar a diferenca e fechar (grava + avisa a
  // gerencia). A contagem NAO muda aqui.
  if (divergFecharPendente) {
    const falta = divergFecharPendente.divergencia < 0;
    const diff = Math.abs(divergFecharPendente.divergencia);
    return (
      <Modal aberto onFechar={onFechar} titulo={`Fechar turno — ${caixa.nome}`} largura={520}
        rodape={<button type="button" className="btn-secundario" onClick={() => setDivergFecharPendente(null)} disabled={enviando}>Voltar</button>}
      >
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{
            padding: '12px 14px', borderRadius: 8, backgroundColor: '#ef4444',
            borderLeft: '6px solid #b91c1c', color: '#000', fontWeight: 700,
          }}>
            A gaveta não bateu — {falta ? 'FALTARAM' : 'SOBRARAM'} {formatMoeda(diff)}. Resolva antes de fechar.
          </div>

          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Você contou</span><span className="cel-num">{formatMoeda(divergFecharPendente.contado)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Esperado na gaveta</span><span className="cel-num">{formatMoeda(divergFecharPendente.esperado)}</span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', marginTop: 4, padding: '8px 12px',
              borderRadius: 8, backgroundColor: '#f59e0b', border: '2px solid #b45309', color: '#000', fontWeight: 700,
            }}>
              <span>{falta ? 'FALTOU' : 'SOBROU'}</span><span className="cel-num">{formatMoeda(diff)}</span>
            </div>
          </div>

          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
            Escolha como resolver pra fechar:
          </p>

          {/* a) Recontar — volta pra contagem (a diferenca pode ser erro de contagem) */}
          <button type="button" className="btn-secundario" onClick={() => setDivergFecharPendente(null)} disabled={enviando}
            style={{ textAlign: 'left', padding: '10px 14px' }}>
            <strong>Recontar a gaveta</strong>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Volta pra contagem e confere cédula a cédula.</div>
          </button>

          {/* b) Registrar a diferenca e fechar — grava a divergencia + avisa a gerencia */}
          <button type="button" className="btn-primario" onClick={enviarFechamento} disabled={enviando}
            style={{ textAlign: 'left', padding: '10px 14px' }}>
            <strong>{enviando ? 'Fechando...' : 'Registrar a diferença e fechar'}</strong>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
              Fecha o turno com a {falta ? 'falta' : 'sobra'} de {formatMoeda(diff)} registrada. A gerência é avisada e confere depois.
            </div>
          </button>
        </div>
      </Modal>
    );
  }

  // Painel de resultado da CONFERENCIA do meio do turno: revela esperado x contado SEM fechar.
  // Se deu diferenca, oferece a lupa (extrato) pra investigar a janela desde a ultima vez que bateu.
  if (resultadoConferencia) {
    const div = resultadoConferencia.divergencia;
    const confere = Math.abs(div) < 0.005;
    return (
      <Modal aberto onFechar={onFechar} titulo={`Conferência — ${caixa.nome}`} largura={480} compacto
        rodape={
          <>
            {!confere && onAbrirExtrato && (
              <button type="button" className="btn-secundario" onClick={() => { onFechar(); onAbrirExtrato(); }}>
                Ver o que entrou desde a última conferência
              </button>
            )}
            {/* Conferiu e bateu: o caminho de quem esta encerrando o expediente vira o botao de
                destaque. "Concluir" (voltar pro turno aberto) continua ali, mas discreto. */}
            {confere && onFecharTurnoAgora
              ? <>
                  <button type="button" className="btn-secundario" onClick={onFechar}>Continuar com o turno aberto</button>
                  <button type="button" className="btn-primario" onClick={onFecharTurnoAgora}>
                    <Lock size={14} style={{ display: 'inline', marginRight: 6, marginBottom: -2 }} />
                    Fechar turno agora
                  </button>
                </>
              : <button type="button" className="btn-primario" onClick={onFechar}>Concluir</button>}
          </>
        }
      >
        <div style={{ display: 'grid', gap: 10 }}>
          <LinhaResumo label="Esperado na gaveta agora" valor={resultadoConferencia.esperado} forte />
          <LinhaResumo label="Você contou" valor={resultadoConferencia.contado} forte />
          <BlocoDivergencia divergencia={div} confere={confere} />
          {/* "O turno continua aberto" JA existia aqui, mas em 12px cinza embaixo do bloco verde de
              "confere" — some ao lado do resultado. Como e a unica linha que distingue conferir de
              fechar, virou faixa de aviso. Conferir NAO fecha: quem nao ler isso deixa a gaveta
              aberta e trava a abertura do dia seguinte. */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6,
            padding: '8px 10px', marginTop: 2,
          }}>
            <AlertTriangle size={16} style={{ color: '#b45309', flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 13, color: '#000', lineHeight: 1.4 }}>
              <strong>Este turno continua ABERTO.</strong> Conferir a gaveta não fecha o caixa.
              {confere
                ? ' Se você está encerrando o expediente, use "Fechar turno agora" — senão a gaveta amanhece aberta e trava a abertura de amanhã.'
                : ' Investigue a diferença pelo extrato — só a janela desde a última vez que bateu.'}
            </span>
          </div>
        </div>
      </Modal>
    );
  }

  // Painel de resultado da PASSAGEM (modo confirmar): revela o que foi deixado x recontado.
  if (resultadoPassagem) {
    const div = resultadoPassagem.divergencia_abertura;
    const confere = Math.abs(div) < 0.005;
    return (
      <Modal aberto onFechar={onFechar} titulo={`Caixa assumido — ${resultadoPassagem.caixa_nome}`} largura={480} compacto
        rodape={<button type="button" className="btn-primario" onClick={onFechar}>Concluir</button>}
      >
        <div style={{ display: 'grid', gap: 10 }}>
          <LinhaResumo label="Deixado por quem saiu" valor={resultadoPassagem.herdado} forte />
          <LinhaResumo label="Você reconferiu" valor={resultadoPassagem.recontado} forte />
          <BlocoDivergencia divergencia={div} confere={confere} />
          {!confere && (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
              A diferença na passagem foi registrada com os dois nomes e o Ruan foi avisado.
            </p>
          )}
        </div>
      </Modal>
    );
  }

  // Painel de resultado (so no fechar): revela esperado x CONTADO + a divergencia (a gaveta foi
  // contada as cegas — DA-334), e mostra o que foi separado pro Cofre e o troco que ficou.
  if (resultado) {
    const divFech = resultado.divergencia;
    return (
      <Modal aberto onFechar={onFechar} titulo={`Turno fechado — ${caixa.nome}`} largura={540}
        rodape={<button type="button" className="btn-primario" onClick={onFechar}>Concluir</button>}
      >
        <div style={{ display: 'grid', gap: 10 }}>
          <LinhaResumo label="Abertura (troco)" valor={resultado.valor_abertura} />
          <LinhaResumo label="Dinheiro recebido" valor={resultado.recebido} />
          {resultado.suprimentos > 0 && <LinhaResumo label="Suprimentos" valor={resultado.suprimentos} />}
          {resultado.sangrias > 0 && <LinhaResumo label="Sangrias" valor={-resultado.sangrias} />}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }} />
          <LinhaResumo label="Esperado em dinheiro" valor={resultado.valor_esperado} forte />
          <LinhaResumo label="Você contou" valor={resultado.valor_contado} forte />
          <BlocoDivergencia divergencia={divFech} confere={Math.abs(divFech) < 0.005} />

          {resultado.transferencia && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <LinhaResumo label={`Separado pro ${resultado.transferencia.destino_nome}`} valor={-resultado.transferencia.valor} />
              <LinhaResumo label="Troco que ficou na gaveta" valor={resultado.troco_final} forte />
            </div>
          )}

          {resultado.sucessor && (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
              Bastão passado para <strong style={{ color: 'var(--text-color)' }}>{resultado.sucessor.nome}</strong> —
              novo turno aberto com {formatMoeda(resultado.troco_final)}.
            </p>
          )}
        </div>
      </Modal>
    );
  }

  const notas = DENOMINACOES_BRL.filter((d) => d.tipo === 'nota');
  const moedas = DENOMINACOES_BRL.filter((d) => d.tipo === 'moeda');
  // O FECHAR e o unico modo em DUAS COLUNAS (DA-396): empilhado ele passava de 1000px de altura e
  // obrigava a rolar justo na hora em que o operador precisa ver contagem, alvo e diferenca juntos —
  // e o passo do Cofre ficava abaixo do corte. Os outros modos tem 2-3 blocos e cabem empilhados.
  const ehFechar = modo === 'fechar';

  const blocoInstrucao = (
    <p style={{ color: 'var(--text-muted)', fontSize: ehFechar ? 12 : 13, margin: ehFechar ? '0 0 10px' : '0 0 12px' }}>
      {modo === 'abrir'
        ? 'Conte o troco e digite a quantidade de cada cédula e moeda.'
        : modo === 'confirmar'
          ? 'Conte o troco que você recebeu na passagem do caixa. A diferença em relação ao que foi deixado aparece depois de confirmar.'
          : modo === 'conferir'
            ? 'O sistema mostra quanto deveria ter na gaveta agora. Conte o dinheiro e veja, ao vivo, se bate — o turno continua aberto.'
            : 'Cédula a cédula. O alvo e a diferença ficam ao lado, sempre à vista — se não bater, o sistema pede pra resolver antes de fechar.'}
    </p>
  );

  const blocoSaldoAnterior = modo === 'abrir' && saldoAnterior != null && saldoAnterior > 0 && (
    <div style={{
      marginBottom: 12, padding: '10px 14px', borderRadius: 8,
      backgroundColor: '#fde68a', border: '2px solid #ca8a04', color: '#000', fontSize: 13,
    }}>
      <strong>O turno anterior deixou {formatMoeda(saldoAnterior)} na gaveta.</strong>{' '}
      Inclua esse valor na sua contagem.
    </div>
  );

  // Conferir (meio do turno) e Fechar (fim) mostram o esperado na gaveta (TRANSPARENTE, DA-334):
  // a operadora conta vendo o alvo e a diferenca ao vivo.
  const blocoEsperado = (modo === 'conferir' || ehFechar) && (
    <div style={{ marginBottom: ehFechar ? 0 : 14, padding: '11px 14px', borderRadius: 8, background: 'var(--accent-soft, rgba(37,99,235,0.06))', border: '1px solid var(--input-border, #e5e7eb)', flex: '0 0 auto' }}>
      {estado ? (
        <div style={{ display: 'grid', gap: 5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15 }}>
            <span>Esperado na gaveta agora</span>
            <span className="cel-num">{formatMoeda(estado.esperado)}</span>
          </div>
          <div style={{ display: 'grid', gap: 2, fontSize: 12, color: 'var(--text-muted)' }}>
            {([['Abertura (troco)', estado.abertura], ['Dinheiro recebido', estado.recebido],
              ...(estado.suprimentos > 0 ? [['Suprimentos', estado.suprimentos] as [string, number]] : []),
              ...(estado.sangrias > 0 ? [['Sangrias', -estado.sangrias] as [string, number]] : [])] as Array<[string, number]>
            ).map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{l}</span><span className="cel-num">{formatMoeda(v)}</span>
              </div>
            ))}
          </div>
          {/* A lupa segue no CONFERIR (investigar diferenca no meio do turno). No fechar ela sairia
              redundante: o grid ao lado ja lista os movimentos, e sem cortar na ultima conferencia. */}
          {modo === 'conferir' && onAbrirExtrato && (
            <button type="button" className="btn-secundario" onClick={onAbrirExtrato} style={{ fontSize: 12, marginTop: 4, justifySelf: 'start' }}>
              Ver movimentos do turno
            </button>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Carregando estado do caixa...</div>
      )}
    </div>
  );

  // Gradinha cedula-a-cedula: TODOS os modos com gaveta contam (abrir / confirmar / conferir /
  // e FECHAR — DA-334). A contagem alimenta o valor_abertura, o marco de conferencia, ou o
  // valor_fechamento + divergencia.
  const blocoContagem = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: ehFechar ? 14 : 16 }}>
      <GradeDenominacoes titulo="Cédulas" itens={notas} gradinha={gradinha} onQtd={setQtd} />
      <GradeDenominacoes titulo="Moedas" itens={moedas} gradinha={gradinha} onQtd={setQtd} />
    </div>
  );

  const blocoTotal = (
    <div style={{
      marginTop: ehFechar ? 12 : 14, padding: '10px 14px', borderRadius: 8, backgroundColor: '#fde68a',
      color: '#000', fontWeight: 700, display: 'flex', justifyContent: 'space-between', flex: '0 0 auto',
    }}>
      <span>Total contado</span>
      <span className="cel-num">{formatMoeda(total)}</span>
    </div>
  );

  // Trava: nao ABRE o turno sem contar a gaveta (abrir zerado por engano perde o troco que ficou
  // do turno anterior). No FECHAR nao ha trava — contagem zero e um FATO e tem que poder fechar
  // ([DA-343]); ela cai no portao da [DA-334], onde a operadora assina a diferenca.
  // A trava daqui ja esteve no `fechar` e anulava a DA-343 inteira: o handler deixava passar,
  // mas o botao ficava cinza e ninguem chegava no portao.
  const blocoAvisoAbrir = modo === 'abrir' && total <= 0 && (
    <p style={{ marginTop: 8, textAlign: 'center', color: '#b45309', fontSize: 12, fontWeight: 600 }}>
      Informe a quantidade de cédulas e moedas para abrir o caixa.
    </p>
  );

  // Diferenca ao vivo — na conferencia do meio do turno E no fechamento (DA-334, transparente):
  // a operadora ve se bate enquanto conta. No fechar, se nao bater, o portao pede pra resolver.
  const blocoDiferenca = (modo === 'conferir' || ehFechar) && estado && (
    <div style={{ marginTop: 10, flex: '0 0 auto' }}>
      {total === 0
        ? <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Conte a gaveta — a diferença aparece aqui ao vivo.</p>
        : <BlocoDivergencia divergencia={diffFechar} confere={Math.abs(diffFechar) < 0.005} />}
    </div>
  );

  // Grid dos movimentos do turno (DA-396). E a UNICA peca que rola: `flex: 1 1 auto` + `min-height`
  // pequeno fazem ela absorver a sobra de altura da coluna e encolher em notebook baixo, enquanto
  // o esperado / o Cofre / o sucessor ficam fixos. O modal em si nunca rola.
  const blocoMovimentos = ehFechar && (
    <div style={{
      flex: '1 1 auto', minHeight: 96, display: 'flex', flexDirection: 'column',
      border: '1px solid var(--input-border, #e5e7eb)', borderRadius: 8, overflow: 'hidden',
    }}>
      <div style={{
        padding: '7px 12px', fontWeight: 600, fontSize: 13, flex: '0 0 auto',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid var(--input-border, #e5e7eb)',
      }}>
        <span>Recebimentos do turno</span>
        {movimentos && movimentos.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>
            {movimentos.length} {movimentos.length === 1 ? 'lançamento' : 'lançamentos'}
          </span>
        )}
      </div>
      <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {movimentos == null ? (
          <p style={{ padding: '12px 14px', margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>Carregando...</p>
        ) : movimentos.length === 0 ? (
          <p style={{ padding: '12px 14px', margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            Nenhum recebimento neste turno — a gaveta tem só o troco da abertura.
          </p>
        ) : (
          <table className="tabela" style={{ width: '100%', fontSize: 12 }}>
            <tbody>
              {movimentos.map((m, i) => (
                <tr key={`${m.ts}-${i}`}>
                  <td className="cel-num" style={{ width: 46, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatHoraSP(m.ts)}</td>
                  <td style={{ lineHeight: 1.3 }}>{m.descricao}</td>
                  <td className="cel-num" style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {m.valor < 0 ? '−' : ''}{formatMoeda(Math.abs(m.valor))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  // Separar as vendas pro Cofre no ato de fechar — Cofre pre-selecionado, VALOR sugerido =
  // esperado - troco de abertura (as vendas do dia). O troco liquido passa adiante. Confirma ou ajusta.
  const blocoCofre = ehFechar && destinosTransferencia && destinosTransferencia.length > 0 && (
    <div style={{ padding: '11px 14px', borderRadius: 8, border: '1px solid var(--input-border, #e5e7eb)', background: 'var(--accent-soft, rgba(37,99,235,0.06))', flex: '0 0 auto' }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 7 }}>Separar dinheiro pro Cofre antes de fechar</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Para</label>
          <select value={transfDestino} onChange={(e) => setTransfDestino(e.target.value)} style={{ width: '100%', marginTop: 2, fontSize: 14, fontWeight: 700 }}>
            <option value="">— Não transferir —</option>
            {destinosTransferencia.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Valor</label>
          <MoneyInput valor={transfValor} onChangeValor={(v) => { setTransfValor(v); setTransfTouched(true); }} style={{ width: '100%', marginTop: 2, fontSize: 14, fontWeight: 700 }} />
        </div>
      </div>
      {transfValor > 0.005 && transfDestino && (() => {
        const troco = +(total - transfValor).toFixed(2);
        const abertura = estado?.abertura ?? 0;
        const trocoCurto = troco < abertura - 0.005;   // deixou menos troco que o padrao da abertura
        return (
          <>
            <div style={{ marginTop: 7, fontSize: 13, fontWeight: 600 }}>
              Fica de troco: <span className="cel-num">{formatMoeda(Math.max(0, troco))}</span>
            </div>
            {trocoCurto && (
              <div style={{
                marginTop: 8, padding: '8px 12px', borderRadius: 8,
                backgroundColor: '#fde68a', border: '2px solid #ca8a04', color: '#000',
                fontSize: 13, fontWeight: 700,
              }}>
                Atenção: o troco está ficando MENOR que a abertura ({formatMoeda(abertura)}).
                A gaveta vai abrir amanhã com {formatMoeda(Math.max(0, troco))} de troco.
              </div>
            )}
          </>
        );
      })()}
    </div>
  );

  const blocoSucessor = ehFechar && (
    <label style={{ display: 'block', flex: '0 0 auto' }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>Passar o caixa para (sucessor):</span>
      <select value={sucessorId} onChange={(e) => setSucessorId(e.target.value)} style={{ width: '100%', marginTop: 4 }}>
        <option value="">— Fim de dia (sem sucessor) —</option>
        {sucessores.map((u) => <option key={u.id} value={u.id}>{u.login}</option>)}
      </select>
    </label>
  );

  return (
    <Modal aberto onFechar={onFechar} largura={ehFechar ? 980 : 560}
      // No fechar o body NAO rola — quem rola e o grid de movimentos. Ver a prop no Modal.
      bodyStyle={ehFechar ? { overflow: 'hidden', display: 'flex', minHeight: 0 } : undefined}
      titulo={`${modo === 'abrir' ? 'Abrir turno' : modo === 'confirmar' ? 'Conferir e assumir o caixa' : modo === 'conferir' ? 'Conferir gaveta' : 'Fechar turno'} — ${caixa.nome}`}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={enviando}>Cancelar</button>
          <button
            type="button"
            className="btn-primario"
            onClick={confirmar}
            disabled={enviando || (modo === 'abrir' && total <= 0)}
            title={modo === 'abrir' && total <= 0 ? 'Conte a gaveta (cédulas e moedas) antes de abrir' : undefined}
          >
            {enviando ? 'Enviando...'
              : modo === 'abrir' ? 'Abrir turno'
              : modo === 'confirmar' ? 'Confirmar recebimento'
              : modo === 'conferir' ? 'Confirmar Valores'
              : 'Fechar turno'}
          </button>
        </>
      }
    >
      {ehFechar ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 18, width: '100%', minHeight: 0 }}>
          {/* Esquerda: o trabalho manual (contar) e o veredito da contagem. */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>1 · Conte a gaveta</div>
            {blocoInstrucao}
            {blocoContagem}
            {blocoTotal}
            {blocoDiferenca}
          </div>
          {/* Direita: o alvo, o extrato do turno (rola) e as duas decisoes do fechamento. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            {blocoEsperado}
            {blocoMovimentos}
            {blocoCofre}
            {blocoSucessor}
          </div>
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          {blocoInstrucao}
          {blocoSaldoAnterior}
          {blocoEsperado}
          {blocoContagem}
          {blocoTotal}
          {blocoAvisoAbrir}
          {blocoDiferenca}
        </div>
      )}
    </Modal>
  );
}

function BlocoDivergencia({ divergencia, confere }: { divergencia: number; confere: boolean }) {
  return (
    <div style={{
      marginTop: 6, padding: '12px 14px', borderRadius: 8,
      backgroundColor: confere ? '#fde68a' : '#f59e0b',
      border: `2px solid ${confere ? '#ca8a04' : '#b45309'}`,
      color: '#000', fontWeight: 700, textAlign: 'center',
    }}>
      {confere
        ? '✓ CONFERIDO — bateu certo'
        : `DIFERENÇA: ${divergencia > 0 ? 'SOBRARAM' : 'FALTARAM'} ${formatMoeda(Math.abs(divergencia))}`}
    </div>
  );
}

function GradeDenominacoes({ titulo, itens, gradinha, onQtd }: {
  titulo: string;
  itens: typeof DENOMINACOES_BRL;
  gradinha: Record<string, number>;
  onQtd: (cents: number, valor: string) => void;
}) {
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>{titulo}</div>
      <div style={{ display: 'grid', gap: 6 }}>
        {itens.map((d) => {
          const qtd = gradinha[String(d.cents)] ?? 0;
          const subtotal = (d.cents * qtd) / 100;
          return (
            <div key={d.cents} style={{ display: 'grid', gridTemplateColumns: '64px 56px 1fr', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13 }}>{d.label}</span>
              <input
                type="number" min={0} step={1} inputMode="numeric"
                value={qtd === 0 ? '' : qtd}
                onChange={(e) => onQtd(d.cents, e.target.value)}
                style={{ width: 56, textAlign: 'right', padding: '4px 6px' }}
                placeholder="0"
              />
              <span className="cel-num" style={{ textAlign: 'right', fontSize: 12, color: subtotal > 0 ? 'var(--text-color)' : 'var(--text-muted)' }}>
                {formatMoeda(subtotal)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LinhaResumo({ label, valor, forte }: { label: string; valor: number; forte?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: forte ? 700 : 400 }}>
      <span>{label}</span>
      <span className="cel-num">{formatMoeda(valor)}</span>
    </div>
  );
}
